// Saga de analytics — observa actions de commerce JÁ EXISTENTES e chama track().
//
// track() é uma função comum (não yield): não bloqueia a saga nem o fluxo do usuário.
// Eventos de mutação (add/remove carrinho, compra) são emitidos APÓS o sucesso da action.
import { all, fork, take, takeEvery, select } from "redux-saga/effects";

import { track } from "../../analytics";
import { mapProductsToItems, sumItemsValue } from "../../analytics/items";

import { Types as SearchTypes } from "../ducks/search";
import { Types as ProductsTypes } from "../ducks/products";
import { Types as CartTypes } from "../ducks/cart";
import { Types as OrderTypes } from "../ducks/order";
import { Types as AuthTypes } from "../ducks/auth";
import { Types as SignUpTypes } from "../ducks/signup";
import { Types as CouponTypes } from "../ducks/coupon";
import { Types as StoreByUserTypes } from "../ducks/storeByUser";

const selectFidelidade = (s) => s.user?.profile?.programa_fidelidade || false;

// ---------- Funil de descoberta ----------

function onSearch({ payload }) {
  const searchTerm = payload?.searchTerm;
  if (!searchTerm) return; // ignora navegação por categoria (sem termo)
  track("search", { search_term: searchTerm });
}

function* onViewItem({ payload }) {
  const product = payload?.data;
  if (!product) return;
  const fidelidade = yield select(selectFidelidade);
  const items = mapProductsToItems([product], fidelidade);
  track("view_item", { items, value: sumItemsValue(items) });
}

function* onViewItemList({ payload }) {
  const list = payload?.data || payload?.dataNewProducts || [];
  if (!list.length) return;
  const fidelidade = yield select(selectFidelidade);
  const items = mapProductsToItems(list, fidelidade);
  track("view_item_list", { items });
}

// ---------- Carrinho (emite APÓS o sucesso, correlacionando request -> success) ----------

function* watchAddToCart() {
  while (true) {
    const req = yield take(CartTypes.ADD_PRODUCT);
    const product = req.payload?.currentProductAdded;

    // Quantidade anterior do produto, capturada ANTES do sucesso (o reducer só altera
    // cart.data em ADD_PRODUCT_SUCCESS). Necessária porque a ação ADD_PRODUCT é
    // sobrecarregada: o carrinho (ProductCard) despacha com sum=false e a quantidade
    // ABSOLUTA nova, incluindo decrementos e remoção (qty 0).
    const prevQty = yield select((s) => {
      const found = (s.cart?.data?.itens || []).find((it) => it.id === product?.id);
      return found ? found.quantidade : 0;
    });

    const res = yield take([CartTypes.ADD_PRODUCT_SUCCESS, CartTypes.ADD_PRODUCT_ERROR]);
    if (res.type !== CartTypes.ADD_PRODUCT_SUCCESS || !product) continue;

    const qty = req.payload?.qty ?? 1;
    const sum = req.payload?.sum !== false; // creator default é true
    // sum=true: qty é incremento; sum=false: qty é a quantidade absoluta nova.
    const delta = sum ? qty : qty - prevQty;
    if (!delta) continue; // sem mudança de quantidade -> nenhum evento

    const fidelidade = yield select(selectFidelidade);
    const evento = delta > 0 ? "add_to_cart" : "remove_from_cart";
    const items = mapProductsToItems(
      [{ ...product, quantidade: Math.abs(delta) }],
      fidelidade
    );
    track(evento, { items, value: sumItemsValue(items) });
  }
}

function* watchAddListToCart() {
  while (true) {
    const req = yield take(CartTypes.ADD_PRODUCT_LIST);
    const res = yield take([CartTypes.ADD_PRODUCT_LIST_SUCCESS, CartTypes.ADD_PRODUCT_ERROR]);
    if (res.type !== CartTypes.ADD_PRODUCT_LIST_SUCCESS) continue;

    const fidelidade = yield select(selectFidelidade);
    const list = req.payload?.currentProductListAdded || [];
    const products = list
      .filter((entry) => entry && entry.product)
      .map(({ product, quantityToBeAdded }) => ({
        ...product,
        quantidade: quantityToBeAdded,
      }));
    if (!products.length) continue;
    const items = mapProductsToItems(products, fidelidade);
    track("add_to_cart", { items, value: sumItemsValue(items) });
  }
}

function* watchRemoveFromCart() {
  while (true) {
    const req = yield take(CartTypes.REMOVE_PRODUCT);
    const res = yield take([CartTypes.REMOVE_PRODUCT_SUCCESS, CartTypes.REMOVE_PRODUCT_ERROR]);
    if (res.type !== CartTypes.REMOVE_PRODUCT_SUCCESS) continue;

    const fidelidade = yield select(selectFidelidade);
    const product = req.payload?.currentProductRemoved;
    if (!product) continue;
    const items = mapProductsToItems(
      [{ ...product, quantidade: product.quantidade || 1 }],
      fidelidade
    );
    track("remove_from_cart", { items, value: sumItemsValue(items) });
  }
}

// ---------- Compra ----------

function* onPurchase({ payload }) {
  // IMPORTANTE: captura o carrinho ANTES de qualquer efeito assíncrono.
  // Em completeOrder (store/sagas/order.js), ORDER_SUCCESS é despachado logo antes
  // de cart/SEND_ORDER_API_SUCCESS, que limpa o carrinho. Os selects abaixo resolvem
  // sincronamente dentro do mesmo dispatch, com o carrinho ainda intacto.
  const cart = yield select((s) => s.cart?.data);
  const fidelidade = yield select(selectFidelidade);
  const storeByUser = yield select((s) => s.storeByUser?.data);

  const data = payload?.data || {};
  const transaction_id = data.orders?.[0]?.id;
  const value = data.payment?.valorTotal;
  const forma_pagamento = data.payment?.formaPagamento;

  const items = mapProductsToItems(cart?.itens || [], fidelidade);
  const loja = storeByUser?.nomeFantasia || storeByUser?.nome;

  track("purchase", {
    items,
    ...(value != null ? { value } : {}),
    ...(transaction_id ? { transaction_id } : {}),
    ...(forma_pagamento ? { forma_pagamento } : {}),
    ...(loja ? { loja } : {}),
  });
}

// ---------- Conta ----------

function onLogin() {
  track("login", { method: "password" });
}

function onSignUp() {
  track("sign_up", { method: "password" });
}

// ---------- Custom farma ----------

function onCoupon({ payload }) {
  const data = payload?.data;
  const coupon = data?.data?.nome || data?.nome;
  track("aplicar_cupom", { ...(coupon ? { coupon } : {}) });
}

function onCouponFail() {
  track("cupom_invalido", {});
}

function onSelectStore({ payload }) {
  const loja = payload?.store?.nomeFantasia || payload?.store?.nome;
  track("selecionar_loja", { ...(loja ? { loja } : {}) });
}

// "Avise-me quando chegar" — lead capturado após o sucesso do cadastro de notificação.
function onGenerateLead() {
  track("generate_lead", { lead_source: "avise_me" });
}

export default function* analyticsSaga() {
  yield all([
    takeEvery(SearchTypes.SEARCH_PRODUCTS_REQUEST, onSearch),
    takeEvery(ProductsTypes.SHOW_SUCCESS, onViewItem),
    takeEvery(
      [ProductsTypes.GET_SUCCESS, ProductsTypes.GET_NEW_PRODUCT_SUCCESS],
      onViewItemList
    ),
    fork(watchAddToCart),
    fork(watchAddListToCart),
    fork(watchRemoveFromCart),
    takeEvery(OrderTypes.ORDER_SUCCESS, onPurchase),
    takeEvery(AuthTypes.SIGN_IN_SUCCESS, onLogin),
    takeEvery(SignUpTypes.SIGN_UP_SUCCESS, onSignUp),
    takeEvery(CouponTypes.GET_COUPON_SUCCESS, onCoupon),
    takeEvery(CouponTypes.GET_COUPON_FAILURE, onCouponFail),
    takeEvery(StoreByUserTypes.SET_SELECTED_STORE, onSelectStore),
    takeEvery(ProductsTypes.GET_NOTIFY_PRODUCT_STOCK_SUCCESS, onGenerateLead),
  ]);
}
